import {
  Alert,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useEffect, useState } from 'react'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import {
  useOrderSplits,
  useSaveOrderSplits,
  useSetSplitStatus,
  useUpdateOrderSplit,
} from '@/api/hooks'
import type { DeliverySplit, OrderDetail } from '@/api/types'
import { useAuthStore } from '@/auth/authStore'
import { ErrorBanner, formatDateTime } from '@/components/common'

/** The lines of business that deliver. A banquet is served where it is booked. */
const DELIVERS = ['OUTDOOR_CATERING', 'BULK_TAKEAWAY']

type Draft = {
  pax: string
  scheduledAt: string
  contactName: string
  contactMobile: string
  packingNote: string
}

const BLANK: Draft = { pax: '', scheduledAt: '', contactName: '', contactMobile: '', packingNote: '' }

const toDraft = (split: DeliverySplit): Draft => ({
  pax: String(split.pax),
  scheduledAt: split.scheduledAt ? split.scheduledAt.slice(0, 16) : '',
  contactName: split.contactName ?? '',
  contactMobile: split.contactMobile ?? '',
  packingNote: split.packingNote ?? '',
})

/**
 * Splitting one sitting into several parcels.
 *
 * <p>An order for a hundred is not always a hundred in one place at one time: a
 * takeaway customer wants fifty at noon for the office and fifty at four for
 * the site. The kitchen cooks a hundred either way - what changes is that it
 * has to be packed as two fifties, labelled, and sent twice.</p>
 *
 * <p>The head counts must add up to what was ordered, and the panel says so as
 * you type rather than at the end: a split that falls ten short leaves ten
 * people unfed, and nobody finds out until the van has gone.</p>
 */
export function DeliverySplitPanel({ order }: { order: OrderDetail }) {
  const canWrite = useAuthStore((state) => state.canWrite)
  const { data, error } = useOrderSplits(order.id)
  const save = useSaveOrderSplits()
  const correct = useUpdateOrderSplit()
  const setStatus = useSetSplitStatus()

  const writable = canWrite('order') && DELIVERS.includes(order.serviceType)
  if (!DELIVERS.includes(order.serviceType)) return null

  return (
    <>
      <ErrorBanner error={error ?? save.error ?? correct.error ?? setStatus.error} />
      {order.sessions.map((session) => (
        <SittingSplits
          key={session.id}
          session={session}
          splits={data?.[session.id] ?? []}
          writable={writable}
          busy={save.isPending}
          onSave={(splits) => save.mutate({ orderId: order.id, eventId: session.id, splits })}
          onStatus={(splitId, status) =>
            setStatus.mutate({ orderId: order.id, splitId, status })
          }
          onCorrect={(splitId, body) =>
            correct.mutate({ orderId: order.id, splitId, body })
          }
        />
      ))}
    </>
  )
}

function SittingSplits({
  session,
  splits,
  writable,
  busy,
  onSave,
  onStatus,
  onCorrect,
}: {
  session: OrderDetail['sessions'][number]
  splits: DeliverySplit[]
  writable: boolean
  busy: boolean
  onSave: (splits: Record<string, unknown>[]) => void
  onStatus: (splitId: string, status: string) => void
  /** One drop put right where the whole set can no longer be rewritten. */
  onCorrect: (splitId: string, body: Record<string, unknown>) => void
}) {
  const [multiParcel, setMultiParcel] = useState(session.multiParcel)
  const [drafts, setDrafts] = useState<Draft[]>([])

  useEffect(() => {
    setMultiParcel(session.multiParcel || splits.length > 0)
    setDrafts(splits.length > 0 ? splits.map(toDraft) : [{ ...BLANK }, { ...BLANK }])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.id, splits])

  const ordered = session.guestCount
  const allotted = drafts.reduce((running, draft) => running + Number(draft.pax || 0), 0)
  const short = ordered - allotted
  // Anything already out of the door is not up for rearranging. That locks the
  // set - the head counts have to keep adding up to what was ordered, and no
  // rearrangement can reach food that has left. It does not lock the drops
  // still standing: the second van's contact number is still worth fixing.
  const gone = splits.some((split) => ['DISPATCHED', 'DELIVERED'].includes(split.status))
  const left = (split?: DeliverySplit) =>
    Boolean(split && ['DISPATCHED', 'DELIVERED'].includes(split.status))

  const update = (index: number, patch: Partial<Draft>) =>
    setDrafts((current) => current.map((draft, at) => (at === index ? { ...draft, ...patch } : draft)))

  return (
    <>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2, mb: 1 }}>
        <Typography variant="subtitle2">
          {session.eventName ?? session.mealPeriod} — {ordered} pax
        </Typography>
        {writable && !gone ? (
          <FormControlLabel
            control={
              <Switch
                checked={multiParcel}
                onChange={(event) => {
                  setMultiParcel(event.target.checked)
                  if (!event.target.checked) onSave([])
                }}
              />
            }
            label="Multi parcel"
          />
        ) : null}
        {gone ? <Chip size="small" color="secondary" label="Dispatch started" /> : null}
      </Stack>

      {!multiParcel ? (
        <Typography variant="caption" color="text.secondary">
          Goes out in one load of {ordered}.
        </Typography>
      ) : (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={90}>Pax</TableCell>
                <TableCell width={200}>When</TableCell>
                <TableCell>Hand to</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>On the label</TableCell>
                <TableCell align="right">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {drafts.map((draft, index) => {
                const saved = splits[index]
                return (
                  <TableRow key={index}>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={draft.pax}
                        disabled={!writable || gone}
                        onChange={(event) => update(index, { pax: event.target.value })}
                        inputProps={{ min: 1, 'aria-label': `Parcel ${index + 1} pax` }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="datetime-local"
                        value={draft.scheduledAt}
                        disabled={!writable || left(saved) || (gone && !saved)}
                        onChange={(event) => update(index, { scheduledAt: event.target.value })}
                        InputLabelProps={{ shrink: true }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={draft.contactName}
                        disabled={!writable || left(saved) || (gone && !saved)}
                        onChange={(event) => update(index, { contactName: event.target.value })}
                        placeholder="Who receives it"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={draft.contactMobile}
                        disabled={!writable || left(saved) || (gone && !saved)}
                        onChange={(event) => update(index, { contactMobile: event.target.value })}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={draft.packingNote}
                        disabled={!writable || left(saved) || (gone && !saved)}
                        onChange={(event) => update(index, { packingNote: event.target.value })}
                        placeholder="Office batch, 5th floor pantry"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {saved ? (
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                          <Chip size="small" variant="outlined" label={saved.status} />
                          {writable && gone && !left(saved) ? (
                            <Tooltip title="Save this drop's details">
                              <IconButton
                                size="small"
                                aria-label={`Save drop ${index + 1}`}
                                onClick={() =>
                                  onCorrect(saved.id, {
                                    pax: saved.pax,
                                    scheduledAt: draft.scheduledAt
                                      ? new Date(draft.scheduledAt).toISOString()
                                      : undefined,
                                    contactName: draft.contactName.trim() || undefined,
                                    contactMobile: draft.contactMobile.trim() || undefined,
                                    packingNote: draft.packingNote.trim() || undefined,
                                  })
                                }
                              >
                                <SaveOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {writable && saved.status !== 'DELIVERED' ? (
                            <Button
                              size="small"
                              onClick={() =>
                                onStatus(
                                  saved.id,
                                  saved.status === 'PLANNED'
                                    ? 'PACKED'
                                    : saved.status === 'PACKED'
                                      ? 'DISPATCHED'
                                      : 'DELIVERED',
                                )
                              }
                            >
                              {saved.status === 'PLANNED'
                                ? 'Packed'
                                : saved.status === 'PACKED'
                                  ? 'Send'
                                  : 'Delivered'}
                            </Button>
                          ) : null}
                        </Stack>
                      ) : writable && !gone ? (
                        <Tooltip title="Remove this parcel">
                          <IconButton
                            size="small"
                            onClick={() => setDrafts((c) => c.filter((_, at) => at !== index))}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>

          {/* Said as you type, not when you save: a short split is the whole
              reason this exists. */}
          <Alert severity={short === 0 ? 'success' : 'warning'} sx={{ mt: 1 }}>
            {short === 0
              ? `${allotted} of ${ordered} allotted. The parcels add up.`
              : short > 0
                ? `${allotted} of ${ordered} allotted — ${short} more to place, or that many go unfed.`
                : `${allotted} allotted against ${ordered} ordered — ${-short} more would be packed than was sold.`}
          </Alert>

          {writable && !gone ? (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setDrafts((current) => [...current, { ...BLANK }])}
              >
                Add parcel
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={short !== 0 || drafts.length < 2 || busy}
                onClick={() =>
                  onSave(
                    drafts.map((draft) => ({
                      pax: Number(draft.pax),
                      scheduledAt: draft.scheduledAt
                        ? new Date(draft.scheduledAt).toISOString()
                        : undefined,
                      contactName: draft.contactName.trim() || undefined,
                      contactMobile: draft.contactMobile.trim() || undefined,
                      packingNote: draft.packingNote.trim() || undefined,
                    })),
                  )
                }
              >
                Save parcels
              </Button>
            </Stack>
          ) : null}

          {gone ? (
            <Typography variant="caption" color="text.secondary">
              A parcel has already gone out, so the split is fixed. Move the remaining ones along
              with the buttons above.
            </Typography>
          ) : null}

          {splits.some((split) => split.dispatchedAt) ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Last dispatch {formatDateTime(splits.find((s) => s.dispatchedAt)?.dispatchedAt)}
            </Typography>
          ) : null}
        </>
      )}
    </>
  )
}
